import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppState } from '../../../../reducers';
import {
  clearState,
  invalidateInitLoad,
  loadCompanyDetails,
  setPageDataRequestIndication,
  updateNavigationIndex,
  updateNavigationPage,
  updateSelectedComparableId,
  updateSelectionData,
} from './drill-down.actions';
import {
  hasDrillDownDetailsLoaded,
  selectCompanyName,
  selectExternalData,
  selectInitLoad,
  selectNavigationData,
  selectCompanyDatabaseName,
  selectCompanyDatabasesCount,
  hasSelectionData,
  selectComparableIdByPageAndIndex,
  selectComparableSelectionData,
  selectCompanyDatabaseId,
} from './drill-down.selectors';

@Component({
  selector: 'app-company-slide-out-drill-down',
  templateUrl: './company-slide-out-drill-down.component.html',
  styleUrls: ['./company-slide-out-drill-down.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class CompanySlideOutDrillDownComponent implements OnInit, OnDestroy {
  companyName$: Observable<string>;

  comparableIdByPageAndIndex: number;
  databaseId: number;
  comparableSelectionData: { currencyId: number; isConsolidated: boolean };
  navigationData: { page: number; index: number };
  externalData: { rows: number; count: number };
  areDetailsLoadedForSelectedCompany: boolean;
  databaseName: string;
  databasesCount: number;
  hasSelectionData: boolean;

  destroy$ = new Subject();

  constructor(private store: Store<AppState>) {}

  ngOnInit(): void {
    this.companyName$ = this.store.pipe(select(selectCompanyName));

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectCompanyDatabaseId))
      .subscribe((id) => {
        this.databaseId = id;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(hasSelectionData))
      .subscribe((hasData) => {
        this.hasSelectionData = hasData;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectComparableSelectionData))
      .subscribe((data) => {
        this.comparableSelectionData = data;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectCompanyDatabaseName))
      .subscribe((name) => {
        this.databaseName = name;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectCompanyDatabasesCount))
      .subscribe((count) => {
        this.databasesCount = count;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectComparableIdByPageAndIndex))
      .subscribe((id) => {
        this.comparableIdByPageAndIndex = id;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectExternalData))
      .subscribe((data) => {
        this.externalData = data;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectNavigationData))
      .subscribe((data) => {
        this.navigationData = data;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(hasDrillDownDetailsLoaded))
      .subscribe((hasDetails) => {
        this.areDetailsLoadedForSelectedCompany = hasDetails;
      });

    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectInitLoad))
      .subscribe((initLoad) => {
        if (initLoad) {
          this.onPageIndexChange(this.navigationData.index);
          this.store.dispatch(invalidateInitLoad());
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
    this.store.dispatch(clearState());
  }

  prev() {
    if (this.navigationData.index - 1 >= 0) {
      this.onPageIndexChange(this.navigationData.index - 1);
    } else {
      this.onPageChange(this.navigationData.page - 1, this.externalData.rows - 1);
    }
  }

  next() {
    if (this.navigationData.index + 1 < this.externalData.rows) {
      this.onPageIndexChange(this.navigationData.index + 1);
    } else {
      this.onPageChange(this.navigationData.page + 1, 0);
    }
  }

  onPageChange(page: number, index: number) {
    this.store.dispatch(updateNavigationPage({ page, index }));

    if (!this.comparableIdByPageAndIndex) {
      this.store.dispatch(setPageDataRequestIndication());
    } else {
      this.onPageIndexChange(index);
    }
  }

  onPageIndexChange(index: number) {
    this.store.dispatch(updateNavigationIndex({ index }));
    this.store.dispatch(updateSelectedComparableId({ id: this.comparableIdByPageAndIndex }));

    if (!this.areDetailsLoadedForSelectedCompany) {
      this.store.dispatch(loadCompanyDetails({ id: this.comparableIdByPageAndIndex, databaseId: this.databaseId }));
    } else if (!this.hasSelectionData) {
      this.store.dispatch(
        updateSelectionData({
          id: this.comparableIdByPageAndIndex,
          databaseId: this.databaseId,
          currencyId: this.comparableSelectionData.currencyId,
          isConsolidated: this.comparableSelectionData.isConsolidated,
        })
      );
    }
  }

  get isNextDisabled(): boolean {
    return !this.externalData || !this.navigationData
      ? false
      : this.externalData.rows * this.navigationData.page + this.navigationData.index === this.externalData.count - 1;
  }

  get isPrevDisabled(): boolean {
    return !this.externalData || !this.navigationData ? false : this.externalData.rows * this.navigationData.page + this.navigationData.index === 0;
  }

  get navigationDisabled(): boolean {
    return this.isNextDisabled && this.isPrevDisabled;
  }

  get showDatabaseName(): boolean {
    return !!this.databaseName && !!this.databasesCount && this.databasesCount === 1;
  }
}
